import React, { useState } from 'react';
import styled from 'styled-components';
import { Field, Grid, GridCol, GridRow, Radio, RichTextEditor, typeStyle, units } from '@m/alchemy-ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormikProps, useFormikContext } from 'formik';
import { Container } from '../../../common/components';
import { MilestoneMessagesType, RTEenterTypes } from '../../../common/enums';
import { Milestone, MilestoneType } from '../../../common/core-api';
import { HelpText, Title } from './MilestoneForm.styled';

const richTextEditorConfig = {
  defaultActionOnPaste: 'insert_clear_HTML',
  buttons: ['bold', 'italic', 'ul', '|', 'undo', 'redo'],
  enter: RTEenterTypes.BR
};

const SubTitle = styled.div`
  ${typeStyle('actionM')};
  margin-bottom: ${units(1)};
`;

export const MilestoneMessageRadio = ({
  title,
  checked,
  onChange,
  messageType
}: {
  title: string;
  checked: boolean;
  onChange: () => void;
  messageType: MilestoneMessagesType;
}) => {
  const intl = useIntl();
  const { values, setFieldValue }: FormikProps<Milestone> = useFormikContext();

  const {
    type,
    voters,
    approachMessageToAuthors,
    approachMessageToUsers,
    approachEmailMessageToModerators,
    approachEmailMessageToVoters,
    reachedEmailMessageToAuthors
  } = values;

  /*
    Rich Text Editor defaultValue stored (and never updated) in state
    If defaultValue is unstable it causes rerenders on every input
  */
  const [defaultValues] = useState({
    approachMessageToAuthors,
    approachMessageToUsers,
    approachEmailMessageToModerators,
    approachEmailMessageToVoters,
    reachedEmailMessageToAuthors
  });

  let emailToVotersDisabled = true;
  if (type === MilestoneType.VOTE) {
    emailToVotersDisabled =
      (voters?.users?.length === 0 && voters?.groups?.length === 0) ||
      Boolean(voters?.groups?.find((group) => group.id === '0'));
  }

  return (
    <Container>
      <Title>{title}</Title>
      <Radio
        label={intl.formatMessage({
          id: 'useDefaultMessage',
          defaultMessage: 'Use default system generated message'
        })}
        name="useDefaultMessage"
        checked={checked}
        onChange={onChange}
      />
      <Radio
        label={intl.formatMessage({
          id: 'useOwnMessage',
          defaultMessage: 'I would like to write my own message'
        })}
        name="useOwnMessage"
        checked={!checked}
        onChange={onChange}
      />
      {messageType === MilestoneMessagesType.APPROACH && !checked && (
        <Grid>
          <Container>
            <GridRow>
              <GridCol span={{ small: 12, medium: 6 }}>
                <Field
                  isFullWidth
                  labelVariant="emphasized"
                  label={intl.formatMessage({
                    id: 'messageForIdeaAuthor',
                    defaultMessage: 'Message for idea author'
                  })}
                  input={
                    <RichTextEditor
                      defaultValue={defaultValues.approachMessageToAuthors || ''}
                      config={richTextEditorConfig}
                      onChange={(value) => setFieldValue('approachMessageToAuthors', value)}
                    />
                  }
                  helpText={
                    <HelpText>
                      {intl.formatMessage({
                        id: 'approachMessageToAuthorsHelpText',
                        defaultMessage:
                          'This message is shown on the idea page, under the milestones. Only IDEA AUTHORS will see it (not all users). You can use this space to provide information or instructions to authors.'
                      })}
                    </HelpText>
                  }
                />
              </GridCol>
              {type !== MilestoneType.SUBMITTED && (
                <>
                  <GridCol span={{ small: 12, medium: 6 }}>
                    <Field
                      isFullWidth
                      labelVariant="emphasized"
                      label={intl.formatMessage({
                        id: 'messageForUsers',
                        defaultMessage: 'Message for users'
                      })}
                      input={
                        <RichTextEditor
                          defaultValue={defaultValues.approachMessageToUsers || ''}
                          config={richTextEditorConfig}
                          onChange={(value) => setFieldValue('approachMessageToUsers', value)}
                        />
                      }
                      helpText={
                        <HelpText>
                          {intl.formatMessage({
                            id: 'approachMessageToUsersHelpText',
                            defaultMessage:
                              'This message is shown on the idea page, under the milestones. Only USERS will see it (not the idea author). You can use this space to provide information or instructions to users.'
                          })}
                        </HelpText>
                      }
                    />
                  </GridCol>
                </>
              )}
            </GridRow>
          </Container>
          {type !== MilestoneType.SUBMITTED && type !== MilestoneType.REFINE && (
            <Container>
              <GridRow>
                <GridCol span={{ small: 12, medium: 6 }}>
                  <Field
                    isFullWidth
                    labelVariant="emphasized"
                    label={intl.formatMessage({
                      id: 'emailForModerators',
                      defaultMessage: 'Email for moderators'
                    })}
                    input={
                      <RichTextEditor
                        defaultValue={defaultValues.approachEmailMessageToModerators || ''}
                        config={richTextEditorConfig}
                        onChange={(value) => setFieldValue('approachEmailMessageToModerators', value)}
                      />
                    }
                    helpText={
                      <HelpText>
                        {intl.formatMessage({
                          id: 'approachEmailMessageToModeratorsHelpText',
                          defaultMessage:
                            'An email will be sent to moderators to tell them that an idea is approaching this milestone. Use this space to add your own text to the email.'
                        })}
                      </HelpText>
                    }
                  />
                </GridCol>
                {type === MilestoneType.VOTE && (
                  <>
                    <GridCol span={{ small: 12, medium: 6 }}>
                      {emailToVotersDisabled ? (
                        <div>
                          <SubTitle>
                            {intl.formatMessage({
                              id: 'emailForVoters',
                              defaultMessage: 'Email for voters'
                            })}
                          </SubTitle>
                          <FormattedMessage
                            id="emailForVotersDisabledMessage"
                            defaultMessage="This option is only available when individuals or groups of users can vote. You cannot send an email when Everyone can vote."
                          />
                        </div>
                      ) : (
                        <Field
                          isFullWidth
                          labelVariant="emphasized"
                          label={intl.formatMessage({
                            id: 'emailForVoters',
                            defaultMessage: 'Email for voters'
                          })}
                          input={
                            <RichTextEditor
                              defaultValue={defaultValues.approachEmailMessageToVoters || ''}
                              config={richTextEditorConfig}
                              onChange={(value) => setFieldValue('approachEmailMessageToVoters', value)}
                            />
                          }
                          helpText={
                            <HelpText>
                              {intl.formatMessage({
                                id: 'approachEmailMessageToVotersHelpText',
                                defaultMessage:
                                  'This email will be sent to the users specified under "Users who can vote".'
                              })}
                            </HelpText>
                          }
                        />
                      )}
                    </GridCol>
                  </>
                )}
              </GridRow>
            </Container>
          )}
        </Grid>
      )}
      {messageType === MilestoneMessagesType.REACH && !checked && (
        <Grid>
          <Container>
            <GridRow>
              <GridCol span={{ small: 12, medium: 6 }}>
                <Field
                  isFullWidth
                  input={
                    <RichTextEditor
                      defaultValue={defaultValues.reachedEmailMessageToAuthors || ''}
                      config={richTextEditorConfig}
                      onChange={(value) => setFieldValue('reachedEmailMessageToAuthors', value)}
                    />
                  }
                  helpText={
                    <HelpText>
                      {intl.formatMessage({
                        id: 'reachedEmailMessageToAuthorsHelpText',
                        defaultMessage:
                          'An email will be sent to the idea author to thell them that their idea has reached this milestone. Use this space to add your own text to the email. Ideally you should explain what the next milestone is and what they need to do to reach it.'
                      })}
                    </HelpText>
                  }
                />
              </GridCol>
            </GridRow>
          </Container>
        </Grid>
      )}
    </Container>
  );
};
